import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { ProjectStatusBadge } from '../components/ProjectStatusBadge';
import { usePendingProjects } from '../hooks/useProjectMutations';
import { useWritableMinistries } from '../hooks/useProjectCatalogs';
import { hasUnsentAdjustments } from '../lib/flags';
import { Breadcrumb } from '@/shared/components/Breadcrumb';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { Card } from '@/shared/components/ui/card';
import { buttonVariants } from '@/shared/components/ui/button';
import { messages } from '@/shared/constants/messages';
import { formatDateTime } from '@/shared/lib/dates';
import { toUserMessage } from '@/shared/lib/errors';
import type { ProjectWithContext } from '@/shared/types/domain';

/**
 * "O que está parado comigo?" (US-16, US-21 · decisão 49) — **rotulada
 * "Rascunhos"** desde que o item de menu "Minhas pendências" saiu da sidebar.
 *
 * O nome mudou; a lista, não. Ela segue com as duas populações, e o porquê do
 * rótulo estreito estar certo mesmo assim está em `messages.projects`: o
 * devolvido já tem a caixa de entrada do Calendário como caminho principal, e o
 * rascunho tem só este. Quem chega aqui vem do botão "Rascunhos" — na barra do
 * Calendário e no cabeçalho de `/projetos` — ou de salvar um rascunho no
 * formulário (que mora sob `/calendario`; ver `LegacyFormRedirect`).
 *
 * Era só rascunho, e o projeto devolvido para ajuste não tinha tela nenhuma que
 * o reencontrasse — a esteira tinha um beco sem saída exatamente no ponto que a
 * métrica de retrabalho (PRD §6.3) mede. As duas populações vivem aqui porque
 * respondem à MESMA pergunta: alguma coisa espera esta pessoa agir.
 *
 * **O crachá é o que impede as duas de virarem uma só.** Sem ele o líder não
 * distingue o que ele mesmo largou pela metade do que alguém está esperando que
 * ele conserte — e o segundo tem prazo, enquanto o primeiro não tem.
 *
 * O escopo desta lista **não vem de graça da RLS** — ele é explícito em
 * `fetchPendingProjects`, e o porquê está no docblock de lá. Para overseer e
 * coordenador sem vínculo de ministério a tela fica vazia, e isso é correto: a
 * pendência de quem analisa mora em `/analise`.
 */
export function DraftsPage() {
  const { data: pending, isPending, error } = usePendingProjects();
  const { data: ministries } = useWritableMinistries();
  const canCreate = (ministries?.length ?? 0) > 0;

  /**
   * A tela tem dois endereços, e a trilha é a única coisa que muda entre eles.
   *
   * Ela vem do PATHNAME, e não de `originCrumb`: aqui a aba dona não é uma
   * inferência, é literalmente o prefixo da rota que a pessoa está usando.
   * Debaixo de `/calendario` a trilha oferece a volta para a grade — que é de
   * onde o botão "Rascunhos" foi clicado —, e a sidebar acende o Calendário
   * pelo mesmo prefixo, sem `owns` nem `fallbackFor` no meio.
   */
  const inCalendar = useLocation().pathname.startsWith('/calendario');

  return (
    <div className="space-y-6">
      <PageHeader
        title={messages.projects.draftsTitle}
        description={messages.projects.draftsDescription}
        /* ⚠️ **Trilha só no endereço do Calendário.** Em `/rascunhos` ela
           continua ausente, e pelo motivo da decisão 97: a aba dona ali é
           "Projetos", que para `leader` e `assistant` não está no menu — a
           trilha apontaria para um lugar que, para eles, não é ponto de partida
           nenhum. Debaixo de `/calendario` o problema não existe: o Calendário
           é a única aba que os cinco papéis têm. */
        breadcrumb={
          inCalendar ? (
            <Breadcrumb
              items={[
                { label: 'Calendário', to: '/calendario' },
                { label: messages.projects.draftsTitle },
              ]}
            />
          ) : undefined
        }
        actions={
          canCreate ? (
            <Link to="/calendario/novo" className={buttonVariants()}>
              <Plus aria-hidden="true" className="size-4" />
              {messages.form.newProject}
            </Link>
          ) : undefined
        }
      />

      {isPending ? <LoadingState label="Carregando seus rascunhos…" /> : null}

      {error ? (
        <EmptyState title="Não foi possível carregar" description={toUserMessage(error)} />
      ) : null}

      {pending && pending.length === 0 ? (
        <EmptyState
          title={messages.empty.drafts}
          description={messages.projects.draftsEmptyHint}
        />
      ) : null}

      {pending && pending.length > 0 ? (
        // Nomeada porque a trilha acima também é uma lista: sem rótulo, quem
        // navega por listas encontra duas sem saber qual é o conteúdo.
        <ul aria-label={messages.projects.draftsTitle} className="stagger space-y-3">
          {pending.map((project) => (
            <li key={project.id}>
              <PendingCard project={project} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Um item da lista. O verbo do botão é o mesmo predicado da porta em
 * `/projetos/:id` (§3.2) — as duas telas nunca dizem coisas diferentes sobre o
 * mesmo projeto.
 */
function PendingCard({ project }: { project: ProjectWithContext }) {
  const isReturned = project.status === 'changes_requested';
  const action = isReturned
    ? hasUnsentAdjustments(project)
      ? messages.projects.adjustContinue
      : messages.projects.pendingAdjust
    : messages.projects.pendingContinueDraft;

  return (
    <Card className="flex flex-col gap-3 p-4 transition-shadow duration-base ease-out hover:shadow-[0_2px_8px_0_rgb(0_0_0/0.06)] sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate font-medium">{project.title?.trim() || 'Rascunho sem título'}</p>
          {/* Só o devolvido carrega crachá: "Rascunho" ao lado de cada item de
              uma lista que já é majoritariamente rascunho é ruído, e gastaria
              justamente o contraste que faz o devolvido saltar. */}
          {isReturned ? <ProjectStatusBadge project={project} /> : null}
        </div>
        <p className="text-sm text-muted-foreground">
          {project.ministry_name} · editado em {formatDateTime(project.updated_at)}
        </p>
      </div>
      <Link
        to={`/calendario/${project.id}/editar`}
        className={buttonVariants({
          variant: isReturned ? 'default' : 'outline',
          className: 'shrink-0',
        })}
      >
        {action}
      </Link>
    </Card>
  );
}
